import { FC, ReactNode, useState, Fragment } from 'react';
import { Rnd } from 'react-rnd';
import styled from 'styled-components';
import tw from 'twin.macro';
import { NavItemsType } from '../types/dataTypes';
import { useMapContext } from '../context/MapContext';
import Icon from './Icon';
import { ROLE_TYPES, STYLED_STATE } from "@/constants";
import { IconSize, Icons, TabsType } from '../enums';
import useUser from '@/api/queries/useUser';
import TooltipInfo from '@/components/TooltipInfo';
import strings from '@/language/Swedish';
import DropDown from './DropDown';
import { PATH } from '@/routes/Paths';
import { useAuth } from '@/context/AuthContext';
import { useProjectContext } from '@/context/ProjectContext';
import useOrientation from '@/helpers/useOrientation';
import useIsMobile from '@/helpers/useIsMobile';
const StyledRoot = styled.div`
  ${tw`flex flex-col flex-1`}
`;

const StyledList = styled.div <{ $isHidden: boolean }>`
  ${tw`absolute flex text-sm my-3 ml-3 sm:ml-0 gap-2 z-20`}

  ${({ $isHidden }) =>
    $isHidden ? tw`animate-slideOutTop` : tw`animate-slideInTop`};
`;

const TabsWrapper = styled.div <{ $isHidden: boolean }>`
  ${tw`flex gap-2 z-20`}

  ${({ $isHidden }) =>
    $isHidden ? tw`animate-slideOutTop` : tw`animate-slideInTop`};
`;

const NavWrapper = styled.div`
  ${tw`flex shrink-0 gap-2`}
`;

const StyledTrigger = styled.button`
  ${tw`cursor-pointer outline-none rounded-lg transition px-3 py-1 sm:h-8 h-7 select-none border border-white bg-white/90 data-[state=active]:bg-slate-800 data-[state=active]:border-slate-900 text-slate-800 data-[state=active]:text-slate-50`}

  &:active {
    ${tw`outline outline-slate-800/20 outline-offset-2 bg-slate-800 border-slate-900 text-white text-slate-50`}
  }

  &:hover {
    ${tw`bg-white text-slate-800 data-[state=active]:bg-slate-800 data-[state=active]:text-white`}
  }
  &:disabled {
      ${tw`opacity-60`}

  }
`;

const StyledMenuTrigger = styled.div`
  ${tw`font-medium block sm:hidden leading-none relative border rounded-full transition duration-200 ease-in-out flex active:outline-offset-2 active:outline active:outline-slate-800/20 [&>*]:relative [&>*]:z-10 after:content-[''] after:transition hover:after:opacity-50 after:absolute after:z-0 after:inset-px after:rounded-full after:shadow-[0_0_0_1px_rgba(255,255,255,0.25)] after:bg-[radial-gradient(100%_100%_at_50%_100%,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0)_100%)] has-[class*='remove']:opacity-10 [&>*]:font-medium items-center justify-center bg-slate-800 border-slate-800 text-white before:border-t-slate-50 before:border-slate-50/5 gap-3 px-2.5 py-2.5 text-base h-7 before:hidden `}
  `;

const StyledMenuTriggerActive = styled.div`
  ${tw`font-medium block sm:hidden leading-none relative border rounded-full transition duration-200 ease-in-out flex active:outline-offset-2 active:outline active:outline-green-800/20 [&>*]:relative [&>*]:z-10 after:content-[''] after:transition hover:after:opacity-50 after:absolute after:z-0 after:inset-px after:rounded-full after:shadow-[0_0_0_1px_rgba(255,255,255,0.25)] after:bg-[radial-gradient(100%_100%_at_50%_100%,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0)_100%)] has-[class*='remove']:opacity-10 [&>*]:font-medium items-center justify-center bg-green-800 border-green-800 text-white before:border-t-green-50 before:border-green-50/5 gap-3 px-2.5 py-2.5 text-base h-7 before:hidden `}
  `;

const ContentContainer = styled.div<{ height?: string }>`
  ${tw`z-10 w-full sm:rounded-2xl border border-white/80 bg-white/40 flex-1`}
  min-height: 0;
  max-height: calc(100vh - ${props => props.height || '550px'} - 45px);
  overflow: hidden;
`;

const Wrapper = styled.div`
  ${tw`flex flex-col flex-1 gap-4`}
`;

const MapContainer = styled.div`
  ${tw`flex-none w-full bg-white/40 border border-white/80 sm:rounded-2xl backdrop-blur`}
`;


const MessageContainer = styled.div`
  ${tw`hidden lg:block bg-white opacity-100 hover:opacity-80 p-2 ml-5 rounded-md text-[12px] cursor-pointer`}
`;



interface Props {
  items: NavItemsType[];
  children?: ReactNode;
  setSelectedTab: (value: string) => void;
  selectedTab: string;
}

const Tabs: FC<Props> = ({ items, children, setSelectedTab, selectedTab }) => {
  const orientation = useOrientation();
  const isMobile = useIsMobile();
  const minHeight = '200px';
  const maxHeight = orientation == "portrait" ? '1200px' : "320px";
  const defaultHeight = `${Math.floor((window.innerHeight - 45) / 2)}px`;
  const [height, setHeight] = useState(defaultHeight);
  const [savedHeight, setSavedHeight] = useState(defaultHeight);
  const { data: userData } = useUser();
  const { isStreetViewOn, isMapVisible, setIsMapVisible, resetMap } = useMapContext();
  const { logout } = useAuth();
  const { resetProject } = useProjectContext();

  const handleLogoutClick = () => {
    resetProject();
    resetMap(true);
    logout();
  };

  const toggleMapVisibility = () => {
    if (isMapVisible) {
      // Save current height before hiding
      setSavedHeight(height);
      setHeight('0px');
    } else {
      // Restore previous height when showing
      setHeight(savedHeight);
    }
    setIsMapVisible(!isMapVisible);
  };
  const topMenuData = {
    trigger: {
      element: <StyledMenuTrigger>
        <Icon className="block sm:hidden" icon={Icons.menu8} />
      </StyledMenuTrigger>,
      activeElement: <StyledMenuTriggerActive>
        <Icon className="block sm:hidden" icon={Icons.cross} />
      </StyledMenuTriggerActive>,
    },
    items: [
      {
        link: PATH.projects,
        text: strings.sidebar.savedProjects,
      },
      {
        text: strings.sidebar.support,
        link: `mailto:${strings.login.emailUrl.email}`,
      },
      { text: strings.login.logout, handleClick: handleLogoutClick },
    ],
  };

  function goTo(href:string){
    window.location.href = href;
  }

  return (
    <StyledRoot>
      <StyledList $isHidden={isStreetViewOn}>
        <DropDown data={topMenuData} imgSize="large" />
        <TabsWrapper $isHidden={isStreetViewOn}>
          <NavWrapper>
            {items.map(({ id, label, icon }) => {
              const noMarketRights = id == TabsType.market && !userData?.roles.includes(ROLE_TYPES.MARKET_SHARE_AD);
              return (
                <TooltipInfo key={id} content={noMarketRights ? strings.market.noMarketRights : undefined}>
                  <StyledTrigger onClick={() => setSelectedTab(id)} data-state={id == selectedTab ? STYLED_STATE.ACTIVE : STYLED_STATE.CLOOSED} disabled={noMarketRights}>
                    <Icon
                      className="block sm:hidden"
                      size={IconSize.medium}
                      icon={icon}
                    />
                    <span className="hidden sm:block"> {label}</span>
                  </StyledTrigger>
                </TooltipInfo>
              )
            })}
            {isMobile && (
              <TooltipInfo content={isMapVisible ? "Dölj karta" : "Visa karta"}>
                <StyledTrigger onClick={toggleMapVisibility}>
                  <Icon
                    size={IconSize.medium}
                    icon={isMapVisible ? Icons.eyeClosed : Icons.eye}
                  />
                </StyledTrigger>
              </TooltipInfo>
            )}
          </NavWrapper>
          <MessageContainer onClick={() => goTo("mailto:info@valueguard.se")}>
            {strings.message}
          </MessageContainer>
        </TabsWrapper>
      </StyledList>
      <Wrapper>
        {isMapVisible && (
          <MapContainer
            style={{
              width: '100%',
              height: height,
              minHeight: minHeight,
              maxHeight: maxHeight,
              border: 0
            }}>
            <Rnd
              size={{ width: '100%', height: height }}
              onResize={(e, direction, ref) => {
                const newHeight = `${ref.style.height}`;
                setHeight(newHeight);
                setSavedHeight(newHeight);
              }}
              enableResizing={{
                top: false,
                right: false,
                bottom: true,
                left: false,
                topRight: false,
                bottomRight: false,
                bottomLeft: false,
                topLeft: false,
              }}
              disableDragging={true}
              minHeight={minHeight}
              maxHeight={maxHeight}
              className="custom-rnd"
              resizeHandleWrapperClass="custom-handle-wrapper">
              <div className="h-full sm:rounded-2xl overflow-clip">
                {children}
              </div>
            </Rnd>
          </MapContainer>
        )}
        <ContentContainer height={isMapVisible ? height : '0px'}>
          {items.map(({ id, location }) => {
            if (id === selectedTab) {
              return <Fragment key={id}>{location}</Fragment>
            }
          })}
        </ContentContainer>
      </Wrapper>
    </StyledRoot>
  );
};

export default Tabs;
